import { useEffect, useCallback, useContext, useState } from "react"
import { DateTime } from "luxon"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//SOAR Components
import { LoaderMedium } from "../../../components/Loaders/index"

//Helpers
import { LogError, LogWarning } from "../../../helpers/logger"

//Housing Styles and components
import { housingApiListNotesPost } from "../housingApi"
import HousingNoteDetail from "./HousingNoteDetail"
import HousingNotesFilter from "./HousingNotesFilter"
import HousingNotesForm from "./HousingNotesForm"
import myStyle from "../../../styles/housing/HousingNotes.module.css"

//PrimeReact
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Tooltip } from "primereact/tooltip"
import { Editor } from "primereact/editor"

export default function HousingNotes({
  participant,
  onSetParticipant,
  houseParticipants,
  activeHouseParticipants,
  users,
  houses,
}) {
  const userCtx = useContext(UserContext)

  let startDate = new Date()
  startDate.setDate(1)
  startDate.setMonth(startDate.getMonth() - 1)

  const [isLoading, setIsLoading] = useState(true)
  const [notes, setNotes] = useState([])
  const [editNote, setEditNote] = useState(undefined)
  const [showForm, setShowForm] = useState(false)
  const [pageSetup, setPageSetup] = useState({ rows: 20, first: 0, page: 0 })
  const [noteFilters, setNoteFilters] = useState({
    houseId: "",
    fromDate: startDate,
    toDate: new Date(),
    participantId: participant?.participant_id,
  })
  const [noteDetail, setNoteDetail] = useState({})
  const [showDetail, setShowDetail] = useState(false)

  const getNoteEntries = useCallback(async () => {
    try {
      setIsLoading(true)

      const startDate = DateTime.fromJSDate(noteFilters.fromDate)
        .toUTC()
        .toISODate()

      const endDate = DateTime.fromJSDate(noteFilters.toDate)
        .set({ hour: 23 })
        .set({ minute: 59 })
        .set({ second: 59 })
        .toUTC()
        .toISO()

      const result = await housingApiListNotesPost(userCtx.tokenHousing, {
        enterpriseId: userCtx.session.user.enterprise_id,
        organizationId: userCtx.session.user.organization_id,
        participantId: noteFilters.participantId,
        houseId: noteFilters.houseId,
        start: startDate,
        end: endDate,
        PageSize: pageSetup.rows,
        PageNum: pageSetup.page + 1,
      })

      setNotes(result)

      setIsLoading(false)
    } catch (error) {
      LogError("Unable to retrieve notes list", error)
      userCtx.setNotificationError("Unable to retrieve notes")
      setNotes([])
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    noteFilters.fromDate,
    noteFilters.toDate,
    noteFilters.participantId,
    noteFilters.houseId,
    userCtx.tokenHousing,
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
    pageSetup.rows,
    pageSetup.page,
    userCtx.setNotificationError,
  ])

  useEffect(() => {
    getNoteEntries()
  }, [getNoteEntries])

  const UserColumnTemplate = (rowData) => {
    let userName = rowData.userId

    if (rowData.userId === "-6") {
      userName = "System"
    }

    if (userName !== "System" && rowData.userId) {
      const user = users?.find((user) => user.staff_id === rowData.userId)

      if (!!user) {
        userName = `${user.staff_name_first} ${user.staff_name_last}`
      } else if (rowData.userId === userCtx.session.user.staff_id) {
        //Get current user details
        userName = `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`
      } else {
        LogWarning(`Unable to find user: ${rowData.userId}`)
        userName = "Unknown"
      }
    }

    return <>{userName}</>
  }

  const ParticipantColumnTemplate = (rowData) => {
    if (!!rowData.participantId) {
      const participant = houseParticipants?.find(
        (participant) => participant.participant_id === rowData.participantId
      )

      return <>{!!participant ? participant.name2 : "Unknown"}</>
    } else {
      return <>Manager log</>
    }
  }

  const NoteDateColumnTemplate = (rowData) => {
    const date = DateTime.fromISO(rowData.date, { zone: "utc" }).toLocal()

    return (
      <>{`${date.toLocaleString()} ${date.toLocaleString(
        DateTime.TIME_SIMPLE
      )} ${date.toFormat("ZZZZ")}`}</>
    )
  }

  const NoteColumnTemplate = (rowData) => {
    const GetDisplayText = (text, maxSize) => {
      if (!!maxSize) {
        let returnValue =
          text.length > maxSize ? text.substring(0, maxSize - 3) + "..." : text

        return returnValue
      } else {
        return text
      }
    }

    return (
      <>
        <Editor
          value={GetDisplayText(rowData.note, 150)}
          className={myStyle.EditorStyle}
          readOnly
          headerTemplate={<></>}
          style={{ width: "100%" }}
        />
      </>
    )
  }

  const NoteDetailTemplate = (rowData) => {
    let disabledParticipant = false
    if (!!rowData.participantId) {
      const participant = houseParticipants?.find(
        (participant) => participant.participant_id === rowData.participantId
      )

      disabledParticipant = !participant?.statusActive
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Tooltip target=".house-note-t-tip" />
        <button
          className={`btn ${myStyle.filterHeaderButton} house-note-t-tip`}
          data-pr-tooltip="View the full note entry"
          onClick={() => {
            setNoteDetail(rowData)
            setShowDetail(true)
          }}
        >
          <span className={`material-icons`}>{"info"}</span>
        </button>

        {rowData.userId === userCtx.session.user.staff_id ? (
          <button
            className={`btn ${myStyle.filterHeaderButton} house-note-t-tip`}
            data-pr-tooltip="Edit this note"
            onClick={() => {
              setEditNote(rowData)
              setShowForm(true)
            }}
            disabled={disabledParticipant}
          >
            <span className={`material-icons`}>{"edit"}</span>
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <HousingNoteDetail
        note={noteDetail}
        showNote={showDetail}
        onHideNote={() => {
          setShowDetail(false)
        }}
        users={users}
        participants={houseParticipants}
      />
      {showForm ? (
        <HousingNotesForm
          showForm={showForm}
          onHideForm={(doRefresh) => {
            setEditNote(undefined)

            if (!!doRefresh) {
              getNoteEntries()
            }

            setShowForm(false)
          }}
          noteProfile={editNote}
          houses={houses}
          participants={activeHouseParticipants}
          activeHouseId={noteFilters.houseId}
          participant={participant}
          onNoteCreated={(notes, forceRefresh) => {
            getNoteEntries()

            setEditNote(undefined)
            setShowForm(false)
          }}
        />
      ) : null}

      <HousingNotesFilter
        houseList={houses}
        isLoading={isLoading}
        onRefreshClick={() => {
          getNoteEntries()
        }}
        onNewClick={() => {
          setEditNote(undefined)
          setShowForm(true)
        }}
        onChangeFilters={setNoteFilters}
        onSetParticipant={onSetParticipant}
        participant={participant}
        participants={houseParticipants}
      />
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <DataTable
          value={notes?.notes}
          lazy
          paginator
          first={(notes.pageNum - 1) * notes.pageSize}
          rows={notes.pageSize}
          totalRecords={notes.numTotal}
          onPage={(setup) => {
            setPageSetup(setup)
          }}
          disabled={isLoading}
        >
          <Column
            header="Date"
            body={NoteDateColumnTemplate}
            style={{ width: "170px" }}
          />
          <Column
            header="Staff"
            body={UserColumnTemplate}
            style={{ minWidth: "160px" }}
          />
          <Column
            header="Participant"
            body={ParticipantColumnTemplate}
            style={{ minWidth: "160px" }}
          />
          <Column
            header="House"
            field="houseName"
            style={{ minWidth: "160px" }}
          />
          <Column header="Note" body={NoteColumnTemplate} />
          <Column
            body={NoteDetailTemplate}
            header=""
            style={{ width: "50px" }}
          />
        </DataTable>
      )}
    </>
  )
}
